/* ============================================================
   SINGSPACE ADMIN - PAGE UTILISATEURS (Pro Max)
   ============================================================ */

import { Filter, RefreshCw, Search, ShieldCheck, User, UserCheck, UserPlus, Users } from 'lucide-react';
import { useState } from 'react';
import { KPIWidget } from '../../components/shared/KPIWidget';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { AdminsTable } from '../../components/users/AdminsTable';
import { ClientDetailsModal } from '../../components/users/ClientDetailsModal';
import { ClientsTable } from '../../components/users/ClientsTable';
import { UserFormModal } from '../../components/users/UserFormModal';
import { useAdminUsers, useClients } from '../../hooks/useUsers';

type Tab = 'admins' | 'clients';

export function UsersPage() {
  const [activeTab, setActiveTab] = useState<Tab>('admins');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);

  const adminUsers = useAdminUsers();
  const clients = useClients(currentPage, 10, searchTerm);

  const handleUpdateUser = async (data: any) => {
    if (editingUser) {
      await adminUsers.updateUser(editingUser.id, data);
      setEditingUser(null);
      setIsUserModalOpen(false);
    }
  };

  const handleDeleteUser = async (id: string) => {
    if (confirm('Êtes-vous sûr de vouloir désactiver cet utilisateur ?')) await adminUsers.deleteUser(id);
  };

  const handleRefresh = () => {
    if (activeTab === 'admins') adminUsers.refresh();
    else clients.refresh();
  };

  return (
    <div className="space-y-8 pb-12 animate-in fade-in duration-500">
      {/* --- HEADER --- */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[#00758d] mb-1">
            <Users size={14} />
            <span className="text-[10px] font-black uppercase tracking-widest">Gestion des accès</span>
          </div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900 uppercase">Utilisateurs</h1>
          <p className="text-slate-500 mt-1 font-medium text-sm">
            Gérez les accès administrateurs et suivez votre base client
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleRefresh}
            className="h-11 px-5 rounded-xl border-gray-200 font-bold text-slate-600 hover:bg-gray-50 hover:text-[#00758d] transition-all"
          >
            <RefreshCw size={18} className="mr-2" />
            Actualiser
          </Button>

          {activeTab === 'admins' && (
            <Button
              type="button"
              onClick={() => {
                setEditingUser(null);
                setIsUserModalOpen(true);
              }}
              className="h-11 px-6 rounded-xl bg-[#8E0B56] hover:bg-[#4A041E] text-white font-bold shadow-lg shadow-[#8E0B56]/20 transition-all hover:scale-[1.02] active:scale-95"
            >
              <UserPlus size={18} className="mr-2" />
              Nouvel admin
            </Button>
          )}
        </div>
      </div>

      {/* --- STATS --- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <KPIWidget
          title="Total Administrateurs"
          value={String(adminUsers.users.length)}
          subtext="Équipe de gestion"
          trend="up"
          color="secondary"
          icon={<ShieldCheck />}
        />
        <KPIWidget
          title="Admins Actifs"
          value={String(adminUsers.users.filter((u) => u.isActive).length)}
          subtext="Accès opérationnels"
          trend="up"
          color="primary"
          icon={<UserCheck />}
        />
        <KPIWidget
          title="Base Clients"
          value={String(clients.total)}
          subtext="Clients enregistrés"
          trend="up"
          color="success"
          icon={<User />}
        />
      </div>

      {/* --- MAIN CONTENT --- */}
      <div className="space-y-6">
        {/* TABS */}
        <div className="flex items-center gap-2 p-1.5 bg-slate-100 rounded-xl w-fit">
          <button
            onClick={() => setActiveTab('admins')}
            className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'admins' ? 'bg-white text-[#00303c] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <ShieldCheck size={16} />
            Administrateurs
          </button>
          <button
            onClick={() => setActiveTab('clients')}
            className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'clients' ? 'bg-white text-[#00303c] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <User size={16} />
            Clients
          </button>
        </div>

        {/* FILTERS & SEARCH */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder={`Rechercher par nom ou email...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-11 pl-12 pr-4 rounded-xl border border-gray-200 focus:border-[#00758d] focus:ring-2 focus:ring-[#00758d]/10 outline-none transition-all font-medium text-sm"
            />
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl">
              <Filter size={16} className="text-slate-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="bg-transparent border-none outline-none text-sm font-medium text-slate-600 cursor-pointer"
              >
                <option value="all">Tous les statuts</option>
                <option value="active">Actifs uniquement</option>
                <option value="inactive">Inactifs</option>
              </select>
            </div>
          </div>
        </div>

        {/* TABLES */}
        <Card className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
          {activeTab === 'admins' ? (
            <AdminsTable
              users={adminUsers.users}
              loading={adminUsers.loading}
              error={adminUsers.error}
              statusFilter={statusFilter}
              onEdit={(u) => {
                setEditingUser(u);
                setIsUserModalOpen(true);
              }}
              onDelete={handleDeleteUser}
            />
          ) : (
            <ClientsTable
              clients={clients.items}
              loading={clients.loading}
              error={clients.error}
              statusFilter={statusFilter}
              currentPage={currentPage}
              totalPages={clients.totalPages}
              onPageChange={setCurrentPage}
              onViewDetails={setSelectedClientId}
            />
          )}
        </Card>
      </div>

      <UserFormModal
        isOpen={isUserModalOpen}
        onClose={() => {
          setIsUserModalOpen(false);
          setEditingUser(null);
        }}
        onSubmit={editingUser ? handleUpdateUser : adminUsers.createUser}
        user={editingUser}
        title={editingUser ? "Modifier l'administrateur" : 'Nouvel administrateur'}
      />

      {selectedClientId && (
        <ClientDetailsModal
          isOpen={!!selectedClientId}
          onClose={() => setSelectedClientId(null)}
          clientId={selectedClientId}
        />
      )}
    </div>
  );
}
